import { useCallback } from 'react'
import { useAppDispatch, useAppStore } from '@/hooks'
import { teamApi, TEAM_LIST_ID } from '@/store/api/teamApi'
import { verifyAuth } from '@/store/authSlice'
import { selectTeamDirectoryEnabled } from './useTeamDirectoryEnabled'

export interface TeamInvalidation {
    members?: boolean
    drivers?: boolean
    /**
     * People whose names may have changed (a rename, a removal): their cached names are asked again. When the signed-in
     * user is among them, `/auth/verify` is re-read at every stage: their own record (`user.nylasEmails`) rides on it.
     */
    ids?: readonly string[]
    /**
     * Before the directory serves the company, re-read `/auth/verify` so `application.users` shows the save. For a save
     * whose response does not carry the application back; one that does already refreshed it with `setApplication`.
     */
    reloadRosterWhenOff?: boolean
}

/**
 * After MY save succeeds (create, update, delete, toggle of a member or driver): the lists of that kind are fetched once
 * more, every page and search of it, with no optimistic patching. Other people's changes arrive on the socket instead.
 */
export const useInvalidateTeam = (): ((target: TeamInvalidation) => void) => {
    const dispatch = useAppDispatch()
    const store = useAppStore()
    return useCallback(
        (target: TeamInvalidation) => {
            // Before the directory serves the company the lists come from the roster, re-read with verify when asked; the
            // tags still go, so the scoped driver roster and any full record cached at that stage are fetched once more.
            const state = store.getState()
            const me = state.auth.user?.id
            const savedMe = !!me && (target.ids ?? []).some((id) => String(id) === String(me))
            if (savedMe || (!selectTeamDirectoryEnabled(state) && target.reloadRosterWhenOff)) void dispatch(verifyAuth())
            dispatch(
                teamApi.util.invalidateTags([
                    ...(target.members ? [{ type: 'TeamMembers' as const, id: TEAM_LIST_ID }] : []),
                    ...(target.drivers ? [{ type: 'TeamDrivers' as const, id: TEAM_LIST_ID }] : []),
                    ...(target.ids ?? []).map((id) => ({ type: 'TeamPeople' as const, id })),
                ])
            )
        },
        [dispatch, store]
    )
}
